import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, Host, Inject, Input, NgModule, OnDestroy, OnInit, Optional } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterModule } from '@angular/router';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { applyLanguageFilteringMulti } from '@app/common/filtering-behavior';
import { Utilities } from '@app/common/utilities';
import { AppTileBoxModule } from '@app/components/apps-and-tools/app-tile-box/app-tile-box.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { FeatureToggleModule } from '@app/directives/feature-toggle.directive';
import { HideParentIfEmptyDirective } from '@app/directives/hide-parent-if-empty.directive';
import { AppTile } from '@app/models/app-tile';
import { SimplifiedTerm } from '@app/models/simplified-term';
import { LinkModule } from '@app/directives/link.directive';
import { AppTileService } from '@app/services/app-tile.service';
import { ComponentStatesOnRoutesService } from '@app/services/component-states-on-routes.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { LanguageTermService } from '@app/services/language-term.service';
import { SmartLinksService } from '@app/services/smart-links.service';
import { UserLanguageTermService } from '@app/services/user-language-term.service';
import { Environment } from '@environments/interface.environment';
import { isAfter, isBefore, isSameDay } from 'date-fns';
import { iif, Observable, of, Subject, Subscription } from 'rxjs';
import { catchError, concatMap, filter, first, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { ConfirmDialogComponent, ConfirmDialogModule } from '../confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-smart-links',
  templateUrl: './smart-links.component.html',
  styleUrls: ['./smart-links.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SmartLinksComponent implements OnInit, OnDestroy {

  /** All tiles */
  appTiles$: Observable<AppTile[]>;

  /** Parent component can send edit mode flag, such as when in app center edit mode. */
  @Input() editMode: boolean;
  @Input() maxDescriptionLength = 100;
  private errorSubject$ = new Subject();

  subscriptions = new Subscription();
  languages: Array<SimplifiedTerm>;

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: Environment,
              private smartLinksService: SmartLinksService,
              private appTileService: AppTileService,
              private userLanguageTermService: UserLanguageTermService,
              private featureSettingsStoreService: FeatureSettingsStoreService,
              private componentStatesOnRoutesService: ComponentStatesOnRoutesService,
              private router: Router,
              private elementRef: ElementRef,
              private dialog: MatDialog,
              @Host() @Optional() private hideParentIfEmptyDirective: HideParentIfEmptyDirective,
              private languageTermService: LanguageTermService) { }

  ngOnInit(): void {
    const appTiles$ = this.smartLinksService
      .refresh$
      .pipe(
        mergeMap(() => {
          return iif(
            () => this.editMode,
            this.smartLinksService.getAdminSmartLinks(),
            this.smartLinksService.getUserSmartLinks()
              .pipe(
                map(items => {
                  return items.filter(appTile => {
                    if ((!appTile.End || isAfter(appTile.End, new Date())) && (!appTile.Start || isBefore(appTile.Start, new Date()))) {
                      if (appTile.IsRecurring) {
                        if (appTile.IsWithinTimeSpan) {
                          return appTile;
                        }
                      } else {
                        return appTile;
                      }
                    }
                  });
                })
              )
          );
        }
        ));

    this.appTiles$ = appTiles$
      .pipe(
        switchMap((appTiles) => {
          // Language filter toggle
          return iif(
            () => this.featureSettingsStoreService.state.enableLanguageFilteringForWorkplace && !this.editMode,
            this.userLanguageTermService.getUserLanguageTerms()
              .pipe(
                map((userLanguageTerms) => {
                  return applyLanguageFilteringMulti(appTiles, 'LanguageTermAppTiles', userLanguageTerms, this.featureSettingsStoreService.state.enableShowNonLanguageContentForAllUsers, this.featureSettingsStoreService.state.defaultLanguageTermId);
                })
              ),
            of(appTiles)
          );
        }),
        tap(items => {
          if (items && items.length === 0) {
            this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.hideElement();
          } else {
            this.hideParentIfEmptyDirective && this.hideParentIfEmptyDirective.showElement();
          }
        }),
        catchError(err => {
          this.errorSubject$.next(err);
          return of(null);
        })
      );

    if (!this.editMode) {
      this.subscriptions.add(this.componentStatesOnRoutesService
        .state$
        .pipe(
          filter(componentsOnRouteState => {
            return componentsOnRouteState.RouteMap.has(this.router.url) && componentsOnRouteState.RouteMap.get(this.router.url).some(x => x.ComponentId === this.elementRef.nativeElement.localName && x.Refresh);
          }))
        .subscribe(componentsOnRouteState => {
          if (componentsOnRouteState) {
            this.smartLinksService.reload();
            const componentState = componentsOnRouteState.RouteMap.get(this.router.url).find(x => x.ComponentId === this.elementRef.nativeElement.localName && x.Refresh);
            componentState.Refresh = false;
            if (componentState.Routes.some(r => this.router.url === r)) {
              // If we're on the same route theres no need to show the badge
              componentState.ShowBadge = false;
            }
            this.componentStatesOnRoutesService.updateStates(componentState);
          }
        }));
    }

    if (this.featureSettingsStoreService.state.enableLanguageFilteringForWorkplace && this.editMode) {
      this.subscriptions.add(
        this.languageTermService
          .getLanguages()
          .pipe(
            first()
          )
          .subscribe(l => {
            this.languages = l;
          })
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  remove(appTile: AppTile) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent);
    this.subscriptions.add(dialogRef.afterClosed()
      .pipe(concatMap(result => {
        if (result) {
          return this.appTileService.deleteAppTile(appTile.Id);
        } else {
          return of(null);
        }
      }))
      .subscribe(result => {
        if (result) {
          this.smartLinksService.reload();
        }
      }));
  }

  /** New app info popup */
  showNewAppInfo(event, appTile: AppTile) {
    event.preventDefault();
    this.router.navigate(['', { outlets: { modal: ['app-tile-intro', appTile.Id] } }]);
  }

  /** Util for parsing app url */
  getUrl = (url: string) => Utilities.getRealUrlFromTokenBasedUrl(this.environment, url);

  /** Get text color for tile */
  getColor = (colorHexCode: string) => Utilities.hexToRGBA(colorHexCode, '1');

  /** If "new"-label should show for tile */
  shouldDisplayNew = (appTile: AppTile) => appTile.ShowAsNewUntilThisDate && isAfter(appTile.ShowAsNewUntilThisDate, new Date()) || isSameDay(appTile.ShowAsNewUntilThisDate, new Date());

  /** Resolve app language term id to string */
  getAppLanguages(appLanguages): string {
    if (!this.languages || this.languages.length === 0 || !appLanguages || appLanguages.length === 0) { return ''; }
    const result = new Array<string>();
    appLanguages.forEach(term => {
      const matchingLanguage = this.languages.find(l => l.Guid.toLowerCase() === term.LanguageTermId.toLowerCase());
      if (matchingLanguage && matchingLanguage.Name) {
        result.push(matchingLanguage.Name);
      }
    });
    return ' [' + result.join(', ') + ']';
  }
}
@NgModule({
  declarations: [SmartLinksComponent],
  exports: [SmartLinksComponent],
  imports: [
    CommonModule,
    LinkModule,
    AppTileBoxModule,
    MatMenuModule,
    MatButtonModule,
    WidgetPlaceholderModule,
    FeatureToggleModule,
    ConfirmDialogModule,
    RouterModule
  ]
})
export class SmartLinksModule { }
